import React, { useEffect, useState } from "react";
import { getBrokerProfile } from "../../../Services/BrokersApi";
import {
  Container,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Box,
  Divider,
  Paper,
  Avatar,
} from "@mui/material";
import {
  PendingActions,
  CheckCircle,
  Cancel,
  Business,
  AccountBalance,
  TrendingUp,
  Person,
  Phone,
  LocationOn,
} from "@mui/icons-material";
import { themeColors } from "../../../theme/colors";

const BrokerDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);

  // brokerInfo: استخراج كائن البروفايل الفعلي داخل استجابة الـ API
  // API قد تعيد: { message, data: { data: {...}, pending_demos, accepted_demos, ... } }
  const brokerInfo = profile?.data?.data ?? profile?.data ?? profile;
  const pendingDemos = profile?.pending_demos ?? profile?.data?.pending_demos ?? 0;
  const acceptedDemos = profile?.accepted_demos ?? profile?.data?.accepted_demos ?? 0;
  const rejectedDemos = profile?.rejected_demos ?? profile?.data?.rejected_demos ?? 0;
  const tenantsCount = profile?.tenants_count ?? profile?.data?.tenants_count ?? 0;

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("authToken");

        const data = await getBrokerProfile(null, token);

        // ✅ حفظ البيانات مباشرة (getBrokerProfile يُرجع res.data.data)
        setProfile(data);

        console.log("Broker profile data:", data);
      } catch (err) {
        const msg =
          (err && (err.message || err.msg || JSON.stringify(err))) || "حدث خطأ";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Stats Card Component
  const StatCard = ({ title, value, icon, color, bgColor }) => (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${bgColor} 0%, ${color} 100%)`,
        color: "white",
        borderRadius: 3,
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-8px)",
          boxShadow: `0 12px 24px ${color}40`,
        },
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography
              variant="body2"
              sx={{
                opacity: 0.9,
                fontWeight: 500,
                mb: 1,
              }}
            >
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {value}
            </Typography>
          </Box>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: "rgba(255,255,255,0.25)",
              backdropFilter: "blur(10px)",
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  // Info Row Component
  const InfoRow = ({ icon, label, value }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 1.5,
        px: 2,
        borderRadius: 2,
        transition: "all 0.2s",
        "&:hover": {
          backgroundColor: themeColors.background.hover,
        },
      }}
    >
      <Avatar
        sx={{
          width: 40,
          height: 40,
          bgcolor: `${themeColors.primary.main}15`,
          color: themeColors.primary.main,
        }}
      >
        {icon}
      </Avatar>
      <Box flex={1}>
        <Typography
          variant="caption"
          sx={{
            color: themeColors.text.secondary,
            display: "block",
            mb: 0.5,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: themeColors.text.primary,
          }}
        >
          {value || "-"}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box mb={4}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: themeColors.text.primary,
              mb: 1,
            }}
          >
            مرحباً بك 👋
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: themeColors.text.secondary,
            }}
          >
            نظرة عامة على حسابك وإحصائياتك
          </Typography>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" my={8}>
            <CircularProgress size={60} sx={{ color: themeColors.primary.main }} />
          </Box>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(220, 53, 69, 0.15)",
            }}
          >
            {error}
          </Alert>
        )}

        {!loading && !error && !profile && (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            لا توجد بيانات للبروكر.
          </Alert>
        )}

        {profile && (
          <>
            {/* Statistics Cards */}
            <Grid container spacing={3} mb={4}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="طلبات معلقة"
                  value={pendingDemos}
                  icon={<PendingActions sx={{ fontSize: 28 }} />}
                  color="#FFA726"
                  bgColor="#FFB74D"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="طلبات مقبولة"
                  value={acceptedDemos}
                  icon={<CheckCircle sx={{ fontSize: 28 }} />}
                  color="#66BB6A"
                  bgColor="#81C784"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="طلبات مرفوضة"
                  value={rejectedDemos}
                  icon={<Cancel sx={{ fontSize: 28 }} />}
                  color="#EF5350"
                  bgColor="#E57373"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="عدد النسخ"
                  value={tenantsCount}
                  icon={<Business sx={{ fontSize: 28 }} />}
                  color={themeColors.primary.main}
                  bgColor={themeColors.primary.light}
                />
              </Grid>
            </Grid>

            {/* Profile Information */}
            <Grid container spacing={3}>
              {/* Personal Info */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: `1px solid ${themeColors.border.light}`,
                    height: "100%",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <Avatar
                      sx={{
                        width: 50,
                        height: 50,
                        bgcolor: themeColors.primary.main,
                      }}
                    >
                      <Person sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: themeColors.text.primary,
                      }}
                    >
                      المعلومات الشخصية
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <InfoRow
                    icon={<Person fontSize="small" />}
                    label="الاسم"
                    value={brokerInfo?.name}
                  />
                  <InfoRow
                    icon={<Phone fontSize="small" />}
                    label="الهاتف 1"
                    value={brokerInfo?.phone1}
                  />
                  <InfoRow
                    icon={<Phone fontSize="small" />}
                    label="الهاتف 2"
                    value={brokerInfo?.phone2}
                  />
                  <InfoRow
                    icon={<LocationOn fontSize="small" />}
                    label="المدينة"
                    value={brokerInfo?.city}
                  />
                  <InfoRow
                    icon={<LocationOn fontSize="small" />}
                    label="العنوان"
                    value={brokerInfo?.address}
                  />
                </Paper>
              </Grid>

              {/* Financial Info */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: `1px solid ${themeColors.border.light}`,
                    height: "100%",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <Avatar
                      sx={{
                        width: 50,
                        height: 50,
                        bgcolor: themeColors.success,
                      }}
                    >
                      <AccountBalance sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: themeColors.text.primary,
                      }}
                    >
                      المعلومات المالية
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <InfoRow
                    icon={<TrendingUp fontSize="small" />}
                    label="العمولة"
                    value={brokerInfo?.commetion}
                  />
                  <InfoRow
                    icon={<AccountBalance fontSize="small" />}
                    label="الرصيد"
                    value={brokerInfo?.balance}
                  />
                  <InfoRow
                    icon={<CheckCircle fontSize="small" />}
                    label="المدفوع"
                    value={brokerInfo?.paied}
                  />
                  <InfoRow
                    icon={<PendingActions fontSize="small" />}
                    label="المتبقي"
                    value={brokerInfo?.remmaning}
                  />
                  <InfoRow
                    icon={<AccountBalance fontSize="small" />}
                    label="التأمين"
                    value={brokerInfo?.insurance}
                  />
                </Paper>
              </Grid>

              {/* Account Status */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: `1px solid ${themeColors.border.light}`,
                    background: `linear-gradient(135deg, ${themeColors.background.paper} 0%, ${themeColors.background.default} 100%)`,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: themeColors.text.primary,
                      mb: 3,
                    }}
                  >
                    معلومات الحساب
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box textAlign="center">
                        <Typography
                          variant="caption"
                          sx={{ color: themeColors.text.secondary }}
                        >
                          الحالة
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color:
                              brokerInfo?.status === 1
                                ? themeColors.success
                                : themeColors.error,
                          }}
                        >
                          {brokerInfo?.status === 1 ? "مُفعل ✓" : "غير مُفعل ✗"}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box textAlign="center">
                        <Typography
                          variant="caption"
                          sx={{ color: themeColors.text.secondary }}
                        >
                          تاريخ الإنشاء
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: themeColors.text.primary,
                          }}
                        >
                          {brokerInfo?.created_at
                            ? new Date(brokerInfo.created_at).toLocaleDateString(
                                "ar-EG"
                              )
                            : "-"}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box textAlign="center">
                        <Typography
                          variant="caption"
                          sx={{ color: themeColors.text.secondary }}
                        >
                          آخر تحديث
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: themeColors.text.primary,
                          }}
                        >
                          {brokerInfo?.updated_at
                            ? new Date(brokerInfo.updated_at).toLocaleDateString(
                                "ar-EG"
                              )
                            : "-"}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </Box>
  );
};

export default BrokerDashboardPage;
